import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WebIDData } from '../services/webids';

/**
 * @hook useOnboardingGuard
 * @description Hook para proteger rutas que requieren onboarding completado
 */

interface UseOnboardingGuardOptions {
  webId: WebIDData | null;
  loading: boolean;
  redirectPath?: string;
}

export const useOnboardingGuard = ({ 
  webId, 
  loading, 
  redirectPath 
}: UseOnboardingGuardOptions) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !webId) return;

    // Verificar si el WebID requiere onboarding
    const requiresOnboarding = !webId.onboardingCompleted && 
                              (webId.status === 'active' || webId.status === 'assigned');

    if (requiresOnboarding) {
      const targetPath = redirectPath || `/onboarding/${webId.id}`;
      navigate(targetPath, { replace: true });
    }
  }, [webId, loading, navigate, redirectPath]);

  // Retornar si el onboarding está completado
  return {
    onboardingCompleted: webId?.onboardingCompleted ?? false,
    requiresOnboarding: webId ? 
      !webId.onboardingCompleted && (webId.status === 'active' || webId.status === 'assigned') : 
      false,
  };
};

/**
 * @function checkOnboardingStatus
 * @description Función utilitaria para verificar estado de onboarding
 */
export const checkOnboardingStatus = (webId: WebIDData | null) => {
  if (!webId) return { completed: false, required: false };

  const completed = webId.onboardingCompleted ?? false;
  const required = !completed && (webId.status === 'active' || webId.status === 'assigned');

  return { completed, required };
};

/**
 * @function getOnboardingRedirectPath
 * @description Obtener la ruta de redirección apropiada según el estado del WebID
 */
export const getOnboardingRedirectPath = (webId: WebIDData | null): string | null => {
  if (!webId) return null;

  const { required } = checkOnboardingStatus(webId);
  
  if (required) {
    return `/onboarding/${webId.id}`;
  }

  return null;
};
